import { useState } from 'react';
import { ROTULO_ANDAMENTO, ROTULO_VISTA } from '../dados/catalogo.js';
import { useFonte } from '../dados/contexto.js';
import type { Andamento, VistaDeFoto } from '../dados/fonte.js';
import { Botao, Campo, classeCampo, OpcoesRadio } from './ui/index.js';

const ANDAMENTOS = Object.keys(ROTULO_ANDAMENTO) as Andamento[];

/**
 * Anexar foto ou video a uma ficha, depois da coleta — no escritorio, com os
 * arquivos da camera. O video leva a vista (frente/tras) e o andamento, que vao
 * para `gait_trial`. O arquivo fica no aparelho e sobe quando houver rede.
 */
export function AnexarMidia({ avaliacaoId }: { avaliacaoId: string }) {
  const cadastro = useFonte().cadastro;
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState<'foto' | 'video'>('video');
  const [vista, setVista] = useState<VistaDeFoto>('frente');
  const [andamento, setAndamento] = useState<Andamento | ''>('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (!cadastro) return null;
  if (!aberto) {
    return (
      <Botao tamanho="sm" onClick={() => setAberto(true)}>
        Anexar arquivo
      </Botao>
    );
  }

  const vistas: VistaDeFoto[] = tipo === 'foto' ? ['perfil_esquerdo', 'frente', 'tras'] : ['frente', 'tras'];
  const pronto = !!arquivo && (tipo === 'foto' || !!andamento);

  const anexar = async () => {
    if (!arquivo) return;
    setErro(null);
    setEnviando(true);
    try {
      if (tipo === 'foto') await cadastro.adicionarFoto(avaliacaoId, vista, arquivo);
      else await cadastro.adicionarVideo(avaliacaoId, vista === 'tras' ? 'tras' : 'frente', andamento as Andamento, arquivo);
      setAberto(false);
      setArquivo(null);
      setAndamento('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="w-full space-y-4 rounded-(--radius-painel) border border-borda bg-superficie-2 p-4">
      <OpcoesRadio
        rotulo="Tipo"
        valor={tipo}
        onMudar={(v) => {
          setTipo(v);
          setArquivo(null);
          if (v === 'video' && vista === 'perfil_esquerdo') setVista('frente');
        }}
        opcoes={[
          { valor: 'video', rotulo: 'Vídeo de marcha' },
          { valor: 'foto', rotulo: 'Foto' },
        ]}
      />
      <OpcoesRadio
        rotulo="Vista"
        valor={vista}
        onMudar={setVista}
        opcoes={vistas.map((v) => ({ valor: v, rotulo: ROTULO_VISTA[v] }))}
      />
      {tipo === 'video' && (
        <OpcoesRadio
          rotulo="Andamento"
          obrigatorio
          valor={andamento}
          onMudar={setAndamento}
          opcoes={ANDAMENTOS.map((a) => ({ valor: a, rotulo: ROTULO_ANDAMENTO[a] }))}
        />
      )}
      <Campo rotulo="Arquivo" ajuda="Até 50 MB. O arquivo fica neste aparelho e sobe quando houver internet.">
        {(a) => (
          <input
            {...a}
            type="file"
            accept={tipo === 'foto' ? 'image/*' : 'video/*'}
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            className={classeCampo()}
          />
        )}
      </Campo>
      {erro && <p className="text-sm text-erro">{erro}</p>}
      <div className="flex gap-2">
        <Botao variante="principal" tamanho="sm" disabled={!pronto} carregando={enviando} onClick={() => void anexar()}>
          Anexar
        </Botao>
        <Botao variante="discreto" tamanho="sm" onClick={() => setAberto(false)}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
